import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { getExamMondaiList } from '@/lib/queries';
import { PageSkeleton, ContentRouteError } from '@/components/Skeleton';
import { AppBar } from '@/components/AppBar';
import { MondaiCard } from '@/components/MondaiCard';
import { PageContainer, PageInner } from '@/components/ui/PageContainer';
import { SectionHeader } from '@/components/ui/SectionHeader';
import type { Level } from '@/lib/types';
import { AppError } from '@/lib/errors';

const VALID_LEVELS: Level[] = ['N1', 'N2', 'N3', 'N4', 'N5'];

function parseLevel(raw: string): Level {
  const upper = raw.toUpperCase() as Level;
  if (!VALID_LEVELS.includes(upper)) {
    throw new AppError('Cấp độ không hợp lệ.', { title: 'Đường dẫn không đúng' });
  }
  return upper;
}

export const Route = createFileRoute('/levels/$level/exams/$examSlug')({
  loader: async ({ params }) => {
    const level = parseLevel(params.level);
    const data = await getExamMondaiList(level, params.examSlug);
    if (data.items.length === 0) {
      throw new AppError('Đề thi này chưa có sẵn.', { title: 'Không tìm thấy đề thi' });
    }
    return { ...data, level };
  },
  pendingComponent: () => <PageSkeleton />,
  errorComponent: ({ error, reset }) => <ContentRouteError error={error} reset={reset} />,
  component: ExamMondaiComponent,
});

function formatExamLabel(examSlug: string): string {
  const [yr, mo] = examSlug.split('-');
  return `${mo}/${yr}`;
}

function ExamMondaiComponent() {
  const { level, examSlug, items } = Route.useLoaderData();
  const navigate = useNavigate();
  const label = formatExamLabel(examSlug);
  const totalQuestions = items.reduce((sum, it) => sum + it.question_count, 0);

  return (
    <PageContainer>
      <AppBar
        title={`JLPT ${level} · ${label}`}
        subtitle={`${items.length} phần Mondai · ${totalQuestions} câu hỏi`}
        onBack={() => navigate({ to: '/levels/$level', params: { level } })}
      />

      <PageInner className="pt-5">
        <SectionHeader
          title="Chọn phần luyện"
          caption={`Kỳ thi ${label} có 5 phần Mondai — chọn phần bạn muốn luyện.`}
        />

        <div className="flex flex-col gap-2.5">
          {items.map((it) => (
            <MondaiCard key={it.mondai_type} item={it} examSlug={examSlug} />
          ))}
        </div>
      </PageInner>
    </PageContainer>
  );
}
